import { useEffect, useRef } from 'react';

const brandIdentityItems = [
  {
    icon: 'ri-building-line',
    label: '正式品牌名稱',
    value: '未境原創',
  },
  {
    icon: 'ri-bookmark-line',
    label: '對外簡稱',
    value: '未境原創',
  },
  {
    icon: 'ri-global-line',
    label: '英文名稱',
    value: 'VIJING',
  },
  {
    icon: 'ri-heart-line',
    label: '出版定位',
    value: '愛情小說',
  },
];

export default function BrandIntroSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="min-h-screen-dynamic flex items-center bg-background-50">
      <div ref={ref} className="section-container w-full py-16 md:py-0">
        <div className="text-center mb-10 md:mb-12 max-w-[800px] mx-auto">
          <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
            品牌資訊
          </span>
          <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
            認識未境原創
          </h2>
          <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-500 leading-relaxed">
            以愛情故事延伸不同情感與人生想像
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start max-w-[1000px] mx-auto mb-12">
          <div>
            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 grid grid-cols-2 gap-3 mb-6">
              {brandIdentityItems.map((item) => (
                <div
                  key={item.label}
                  className="p-4 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-50 mb-2">
                    <i className={`${item.icon} text-accent-500 text-sm`}></i>
                  </div>
                  <p className="text-[11px] font-label text-foreground-400 mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-sm font-heading font-medium text-foreground-900 leading-snug">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                未境原創以愛情小說為主要出版方向，透過不同人物、情感與故事情境，呈現愛情關係及人生經歷中的多元面貌。網站提供未境原創的品牌資訊、最新發行與既有小說作品。
              </p>
            </div>
          </div>

          <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Soft%20diffused%20morning%20light%20falling%20on%20an%20open%20book%20resting%20on%20natural%20linen%20fabric%20beside%20a%20simple%20ceramic%20tea%20cup%2C%20warm%20cream%20and%20gentle%20beige%20tones%2C%20subtle%20shadow%20of%20a%20single%20branch%20from%20nearby%20window%2C%20minimalist%20wabi%20sabi%20reading%20nook%20with%20serene%20contemplative%20atmosphere%2C%20delicate%20paper%20texture%20and%20organic%20natural%20materials%2C%20editorial%20photography%20with%20understated%20elegance%20and%20quiet%20warmth&width=800&height=600&seq=brand-info-vijing-2026&orientation=landscape"
                alt="柔和日光下翻開的書本與茶杯"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[1000px] mx-auto">
          <div className="rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div className="space-y-2">
                <h3 className="text-sm font-label font-medium text-foreground-800">
                  品牌經銷與書目查詢
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed max-w-[520px]">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。完整書名、作者與出版資訊，請前往崧博書籍資料庫查詢。
                </p>
              </div>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                前往書籍資料庫
                <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}